import { Card } from '@/lib';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { MarketIntelligenceMetric, MarketIntelligenceReportData } from '../../types';
import { twMerge } from 'tailwind-merge';

const MetricCard: FC<{ metric: MarketIntelligenceMetric; tone: 'success' | 'warning' }> = ({ metric, tone }) => {
  const hasProgressMetric =
    typeof metric.metricValue === 'number' && typeof metric.maxValue === 'number' && Boolean(metric.metricLabel) && Boolean(metric.metricDisplay);
  const progressWidth = hasProgressMetric ? `${Math.min((metric.metricValue / metric.maxValue) * 100, 100)}%` : '0%';

  return (
    <Card className='block border-primary-border bg-white p-4 drop-shadow-none'>
      <p className='text-primary-900 text-sm font-semibold'>{metric.title}</p>
      <p className='text-primary-600 mt-1 min-h-10 text-xs leading-5'>{metric.description}</p>
      {hasProgressMetric ? (
        <div className='mt-4'>
          <div className='text-primary-600 mb-1 flex items-center justify-between text-[11px]'>
            <span>{metric.metricLabel}</span>
            <span className='text-primary-500 font-medium'>{metric.metricDisplay}</span>
          </div>
          <div className='bg-primary-200 h-1.5 overflow-hidden rounded-full'>
            <div className={twMerge('h-full rounded-full', tone === 'success' ? 'bg-green-500' : 'bg-orange-500')} style={{ width: progressWidth }} />
          </div>
        </div>
      ) : null}
    </Card>
  );
};

export const GapAnalysis = ({ report }: { report: MarketIntelligenceReportData }) => {
  const { t } = useTranslation('marketIntelligence');

  return (
    <Card className='block border-primary-border bg-white p-5 drop-shadow-none'>
      <div>
        <h3 className='text-2xl font-semibold text-primary-900'>{t('report.gapAnalysis.title')}</h3>
        <p className='mt-1 text-sm text-primary-600'>{t('report.gapAnalysis.description')}</p>
      </div>

      <div className='mt-5 grid gap-4 md:grid-cols-2'>
        <div className='space-y-3'>
          <p className='flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.08em] text-green-600'>
            <span className='inline-block size-2 rounded-full bg-green-500'></span>
            {t('report.gapAnalysis.strengths')}
          </p>
          {report.strengths.map((metric) => (
            <MetricCard key={metric.id} metric={metric} tone='success' />
          ))}
        </div>

        <div className='space-y-3'>
          <p className='flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-orange-600'>
            <span className='inline-block size-2 rounded-full bg-orange-500'></span>
            {t('report.gapAnalysis.edge')}
          </p>
          {report.competitorsEdge.map((metric) => (
            <MetricCard key={metric.id} metric={metric} tone='warning' />
          ))}
        </div>
      </div>
    </Card>
  );
};
